import { ReactNode, useEffect, useMemo, useState } from "react";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
  DialogPage,
} from "../../../components/modal/Dialog";
import { useFullPageDialogContext } from "../../../components/modal/useFullPageDialogContext";
import { Alert } from "../../../components/alert/Alert";
import {
  RadioGroup,
  RadioGroupOption,
  RadioGroupContainer,
} from "../../../components/radioSelector/RadioGroup";
import { usePriceTierList } from "../../../hooks/api/v2/priceTier/usePriceTierList";
import { usePriceTierConvertToDerived } from "../../../hooks/api/v2/priceTier/usePriceTierConvertToDerived";
import { InfiniteList } from "../../../components/infiniteLoading/InfiniteList";
import { useSupplierRelationshipList } from "../../../hooks/api/v2/supplierRelationship/useSupplierRelationshipList";
import { twMerge } from "tailwind-merge";
import { ProfilePicture } from "../../../components";
import { usePriceTierConvertConnectionsDerived } from "../../../hooks/api/v2/priceTier/usePriceTierConvertConnectionsDerived";
import { useNavigate } from "react-router";
import * as ROUTES from "../../../constants/routes";
import { Button, BadgeZinc } from "@sellar-io/components";
import {
  ConvertDiscountsPages,
  getDiscountDescription,
} from "./BusinessConnectionConvertDiscountsDialog.shared";

type ItemType = {
  id: number;
  name: string;
  logo: string | null;
  description: string;
  priceTierName?: string;
};

export const BusinessConnectionConvertDiscountsDialog = ({
  active,
  setActive,
  discounts,
  connectionId,
}: {
  active: boolean;
  setActive: (active: boolean) => void;
  discounts: ReactNode;
  connectionId: number;
}) => {
  const [name, setName] = useState("");
  const [baseTierId, setBaseTierId] = useState<number>();
  const [baseTierName, setBaseTierName] = useState<string>();

  return (
    <Dialog
      active={active}
      onDismiss={() => setActive(false)}
      initPage={ConvertDiscountsPages.CHOOSE_BASE}
    >
      <DialogPage page={ConvertDiscountsPages.CHOOSE_BASE}>
        {active && (
          <ChooseBase
            onDismiss={() => setActive(false)}
            connectionId={connectionId}
            discounts={discounts}
            name={name}
            setName={setName}
            baseTierId={baseTierId}
            setBaseTierId={setBaseTierId}
            setBaseTierName={setBaseTierName}
          />
        )}
      </DialogPage>
      <DialogPage page={ConvertDiscountsPages.ASSIGN}>
        {active && (
          <Assign
            onDismiss={() => setActive(false)}
            discounts={discounts}
            connectionId={connectionId}
            name={name}
            baseTierId={baseTierId}
            baseTierName={baseTierName}
          />
        )}
      </DialogPage>
    </Dialog>
  );
};

const ChooseBase = ({
  onDismiss,
  connectionId,
  discounts,
  name,
  setName,
  baseTierId,
  setBaseTierId,
  setBaseTierName,
}: {
  onDismiss: () => void;
  connectionId: number;
  discounts: ReactNode;
  name: string;
  setName: (value: string) => void;
  baseTierId?: number;
  setBaseTierId: (value: number) => void;
  setBaseTierName: (value: string) => void;
}) => {
  const { setPage } = useFullPageDialogContext();
  const navigate = useNavigate();
  const [error, setError] = useState("");

  const { data: basePriceLists } = usePriceTierList({
    query: [
      {
        type: "BasePriceTierId",
        modifier: "IS_NULL",
      },
      {
        type: "derivationTypeIncludes",
        value: ["base"],
        modifier: "IN",
      },
    ],
  });

  const flatBaseLists = useMemo(
    () => basePriceLists?.pages.flat() ?? [],
    [basePriceLists],
  );

  useEffect(() => {
    if (flatBaseLists.length > 0 && flatBaseLists[0] && !baseTierId) {
      setBaseTierId(flatBaseLists[0].id);
      setBaseTierName(flatBaseLists[0].name);
    }
  }, [flatBaseLists, setBaseTierName, baseTierId, setBaseTierId]);

  const hasNoBaseLists = flatBaseLists.length === 0;

  const supplierRelationshipListQuery = useSupplierRelationshipList({
    query: [
      { type: "hasCustomerLevelDiscounts", modifier: "EQUALS", value: true },
    ],
    limit: 5,
  });

  const flatConnectionsList = useMemo(
    () =>
      supplierRelationshipListQuery.data?.pages
        .flat()
        .filter((connection) => connection.id !== connectionId) ?? [],
    [supplierRelationshipListQuery.data, connectionId],
  );

  const convertToDerivedPriceList = usePriceTierConvertToDerived();

  const onConfirm = async () => {
    setError("");
    if (!baseTierId) return onDismiss();

    try {
      const derived = await convertToDerivedPriceList.mutateAsync({
        name,
        BasePriceTierId: baseTierId,
        SupplierRetailerConnectionId: connectionId,
      });

      navigate(ROUTES.PRICE_LIST.href({ priceListId: derived.id }));

      onDismiss();
    } catch {
      setError("Failed to convert connections, try again");
    }
  };

  const handleNext = () => {
    if (hasNoBaseLists) onDismiss();
    if (flatConnectionsList.length === 0) return onConfirm();

    setError("");
    if (!baseTierId) {
      setError("Please select a base price list");
      return;
    }

    setPage(ConvertDiscountsPages.ASSIGN);
  };

  return (
    <>
      <DialogNavigation
        title="New derived price list"
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={onDismiss}
      />
      <DialogContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <InputLabel>Name</InputLabel>
          <InputContainer>
            <InputText
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </InputContainer>
        </div>
        {error && <Alert type="error" message={error} />}
        <div className="flex flex-col gap-2">
          <InputLabel>Select base price list</InputLabel>
          {hasNoBaseLists ? (
            <Alert
              type="information"
              title="You haven't set a base price list"
              message="You need to set a base price to derive your price lists from. Visit 'Pricing' to create your base price list and get started with your new pricing tools"
            >
              <Button
                color="secondary"
                className="mt-4 border-blue-800 bg-transparent text-blue-800 hover:bg-blue-200"
                onClick={() => navigate(ROUTES.PRICE_LISTS)}
              >
                Go to pricing
              </Button>
            </Alert>
          ) : (
            <RadioGroup
              value={baseTierId?.toString()}
              onChange={(value) => {
                const id = parseInt(value);
                setBaseTierId(id);
                const baseTier = flatBaseLists.find(
                  (baseList) => baseList.id === id,
                );
                setBaseTierName(baseTier?.name ?? "");
              }}
            >
              <RadioGroupContainer>
                {flatBaseLists.map((pl) => (
                  <RadioGroupOption key={pl.id} value={pl.id.toString()}>
                    {pl.name}
                  </RadioGroupOption>
                ))}
              </RadioGroupContainer>
            </RadioGroup>
          )}
        </div>
        <div className="rounded-lg bg-zinc-100 p-4">{discounts}</div>
      </DialogContent>
      <DialogCTA>
        <Button
          onClick={handleNext}
          disabled={supplierRelationshipListQuery.isPending}
          loading={convertToDerivedPriceList.isPending}
        >
          {flatConnectionsList.length === 0
            ? "Confirm"
            : hasNoBaseLists
              ? "Close"
              : "Next"}
        </Button>
      </DialogCTA>
    </>
  );
};

const Assign = ({
  onDismiss,
  discounts,
  connectionId,
  name,
  baseTierId,
  baseTierName,
}: {
  onDismiss: () => void;
  discounts: ReactNode;
  connectionId: number;
  name: string;
  baseTierId?: number;
  baseTierName?: string;
}) => {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [connectionIds, setConnectionIds] = useState<number[]>([]);

  const toggleConnection = (connection: ItemType) => {
    setConnectionIds((prev) =>
      prev.includes(connection.id)
        ? prev.filter((connectionId) => connectionId !== connection.id)
        : [...prev, connection.id],
    );
  };

  const supplierRelationshipListQuery = useSupplierRelationshipList({
    query: [
      { type: "hasCustomerLevelDiscounts", modifier: "EQUALS", value: true },
    ],
    limit: 5,
  });

  const connectionList = supplierRelationshipListQuery.data?.pages.flat() ?? [];

  const convertToDerivedPriceList = usePriceTierConvertToDerived();
  const convertConnectionsToDerived = usePriceTierConvertConnectionsDerived();

  const onConfirm = async (assignOtherCustomers: boolean) => {
    setError("");
    if (!baseTierId) return onDismiss();

    try {
      const derived = await convertToDerivedPriceList.mutateAsync({
        name,
        BasePriceTierId: baseTierId,
        SupplierRetailerConnectionId: connectionId,
      });

      if (connectionIds.length > 0 && assignOtherCustomers) {
        await convertConnectionsToDerived.mutateAsync({
          id: derived.id,
          connectionIds,
        });
      }

      navigate(ROUTES.PRICE_LIST.href({ priceListId: derived.id }));

      onDismiss();
    } catch {
      setError("Failed to convert connections, try again");
    }
  };

  return (
    <>
      <DialogNavigation
        title="Assign this pricing to more customers?"
        description="The customers listed currently have customer-level discounts applied. If their pricing matches this new price list, you can choose to assign it to them now"
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={onDismiss}
      />
      <DialogContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 rounded-lg bg-zinc-100 p-4">
          <p className="font-semibold">
            Base price list: <span className="font-normal">{baseTierName}</span>
          </p>
          <hr />
          <div>
            <span className="font-semibold">Discounts:</span>
            {discounts}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between gap-4 rounded-t-lg border border-b-0 border-zinc-300 bg-zinc-50 px-4 py-2">
            <span className="flex text-xs font-semibold">
              Customer name + discounts
            </span>

            <span className="flex text-xs font-semibold">
              Current price tier
            </span>
          </div>
          <InfiniteList
            query={supplierRelationshipListQuery}
            className="flex w-full flex-col rounded-b-lg border border-zinc-300"
          >
            {connectionList
              .filter((c) => c.id !== connectionId)
              .map((connection, idx) => (
                <Item
                  key={idx}
                  item={{
                    id: connection.id,
                    name: connection.Buyer.Business.name,
                    logo: connection.Buyer.Business.logoUrl,
                    description: getDiscountDescription(connection),
                    priceTierName: connection.PriceTier?.name,
                  }}
                  selected={connectionIds.includes(connection.id)}
                  onToggle={toggleConnection}
                  noBorder={idx + 1 === connectionList.length}
                />
              ))}
          </InfiniteList>
        </div>

        <p>
          <span className="font-semibold">Please note:</span> You can always
          assign this price list to any of these customers at a later date from
          their customer page, or from the price list
        </p>

        {error && <Alert type="error" message={error} />}
      </DialogContent>
      <DialogCTA>
        <Button color="transparent" onClick={() => onConfirm(false)}>
          Skip
        </Button>
        <Button onClick={() => onConfirm(true)}>Confirm</Button>
      </DialogCTA>
    </>
  );
};

const Item = ({
  item,
  selected,
  onToggle,
  noBorder = false,
}: {
  item: ItemType;
  selected: boolean;
  onToggle: (item: ItemType) => void;
  noBorder?: boolean;
}) => {
  return (
    <div
      className={twMerge(
        "flex items-center gap-4 border-b border-zinc-300 p-4",
        noBorder && "border-b-0",
      )}
    >
      <ProfilePicture logoUrl={item.logo} fallback="supplier-badge" />
      <div className="flex flex-col">
        <span className="flex items-center gap-2 font-semibold">
          {item.name}
        </span>
        <span className="text-sm">{item.description}</span>
      </div>

      <div className="ml-auto flex items-center gap-3">
        {item.priceTierName && (
          <BadgeZinc icon="TAG">{item.priceTierName}</BadgeZinc>
        )}
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggle(item)}
          className="ml-auto"
        />
      </div>
    </div>
  );
};
